import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import { forgotPassword, resetPassword } from "../api/auth";

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [identifier, setIdentifier] = useState(""); // email or username (login only)
  const [email, setEmail] = useState(""); // email for forgot/reset
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const { dark, toggle } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 401 || status === 403
          ? "Invalid credentials. Please check your email/username and password."
          : err.response?.data?.message || "Could not sign in. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      toast("If the email exists, a recovery code has been sent.", "success");
      setMode("reset");
    } catch (err) {
      toast(err.response?.data?.message || "Could not send recovery code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword(email, otp, newPassword);
      toast("Password reset. Please sign in.", "success");
      setMode("login");
      setPassword("");
      setOtp("");
      setNewPassword("");
    } catch (err) {
      toast(err.response?.data?.message || "Reset failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-dark-bg px-4">
      {/* Theme toggle */}
      <button
        onClick={toggle}
        className="fixed top-4 right-4 rounded-lg p-2 text-dark-muted hover:bg-dark-surface hover:text-dark-text transition-colors"
      >
        {dark ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="card w-full max-w-sm p-8">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-accent flex items-center justify-center shadow-lg">
            <span className="text-2xl font-bold text-white">D</span>
          </div>
          <div className="text-center">
            <h1 className="text-xl font-bold text-dark-text dark:text-dark-text">DocMind</h1>
            <p className="text-xs text-dark-muted mt-0.5">Private AI Knowledge Platform</p>
          </div>
        </div>

        {mode === "login" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-dark-muted">Email or Username</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="input-field"
                placeholder="username"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-dark-muted">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p className="rounded-lg bg-red-900/20 border border-red-800/40 px-3 py-2 text-xs text-red-400">
                {error}
              </p>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <button
              type="button"
              onClick={() => setMode("forgot")}
              className="block w-full text-center text-xs text-dark-muted hover:text-dark-text"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-xs text-dark-muted">
              Enter your account email. If it exists, we'll send a 6-digit code.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="you@example.com"
              required
              autoFocus
            />
            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? "Sending…" : "Send recovery code"}
            </button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className="block w-full text-center text-xs text-dark-muted hover:text-dark-text"
            >
              Back to sign in
            </button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={handleReset} className="space-y-4">
            <p className="text-xs text-dark-muted">
              Enter the 6-digit code sent to your email and choose a new password.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="Email"
              required
            />
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="input-field"
              placeholder="6-digit code"
              required
              maxLength={6}
              autoFocus
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
              placeholder="New password (min 8 chars)"
              required
              minLength={8}
            />
            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? "Resetting…" : "Reset password"}
            </button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className="block w-full text-center text-xs text-dark-muted hover:text-dark-text"
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
